import React from 'react'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { useNavigate } from 'react-router-dom'

/**
 * DefaultDashboard - Landing page genérica para usuarios sin admin.administrar
 * Muestra dinámicamente solo las acciones según los permisos que tenga
 * No asume rol específico (supervisor, operador, etc.)
 */
const DefaultDashboard: React.FC = () => {
  const { user: usuario, tienePermiso } = useAuth()
  const { colors } = useTheme()
  const navigate = useNavigate()

  // Mapeo completo de permisos a acciones
  // Agregar aquí nuevos permisos según se creen
  const accionesDisponibles = [
    {
      id: 'reservas',
      permiso: 'reservas.ver',
      titulo: '📅 Reservas',
      descripcion: 'Ver y gestionar reservas',
      ruta: '/admin/reservas',
    },
    {
      id: 'pagos',
      permiso: 'pagos.ver',
      titulo: '💳 Pagos',
      descripcion: 'Ver estado de pagos',
      ruta: '/admin/pagos',
    },
    {
      id: 'qr-scanner',
      permiso: 'qr.validar',
      titulo: '📱 Validador QR',
      descripcion: 'Validar entrada de huéspedes',
      ruta: '/admin/qr-scanner',
    },
    {
      id: 'qr-localizador',
      permiso: 'qr.ver',
      titulo: '📍 Localizador de Espacios',
      descripcion: 'Ubicar espacios ocupados',
      ruta: '/admin/qr-localizador',
    },
    {
      id: 'bloqueos',
      permiso: 'bloqueos.ver',
      titulo: '🔒 Bloqueos',
      descripcion: 'Gestionar bloqueos de espacios',
      ruta: '/admin/bloqueos',
    },
    {
      id: 'reportes',
      permiso: 'reportes.ver',
      titulo: '📈 Reportes',
      descripcion: 'Ver reportes del sistema',
      ruta: '/admin/reportes',
    },
  ]

  // Filtrar solo las acciones que el usuario tiene permiso
  const accionesAutorizadas = accionesDisponibles.filter(accion =>
    tienePermiso(accion.permiso)
  )

  const handleNavegar = (ruta: string) => {
    navigate(ruta)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* Bienvenida */}
          <div
            className="text-white rounded-xl p-8"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark}, ${colors.primary})`,
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-2">
                  ¡Bienvenido, {usuario?.nombre_completo}!
                </h1>
                <p className="text-lg opacity-90">
                  Acceso a funciones específicas del sistema
                </p>
              </div>
              <div className="text-6xl">👤</div>
            </div>
          </div>

          {/* Acciones disponibles */}
          {accionesAutorizadas.length > 0 ? (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-900">
                Acceso rápido a funciones
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {accionesAutorizadas.map((accion) => (
                  <button
                    key={accion.id}
                    onClick={() => handleNavegar(accion.ruta)}
                    className="p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-left bg-white border-2 hover:border-opacity-100 border-opacity-50"
                    style={{
                      borderColor: colors.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.primary}05`
                      e.currentTarget.style.transform = 'translateY(-2px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    <div className="text-3xl mb-3">{accion.titulo.split(' ')[0]}</div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.primary }}>
                      {accion.titulo.split(' ').slice(1).join(' ')}
                    </h3>
                    <p className="text-gray-600 text-sm">{accion.descripcion}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="rounded-lg p-8 text-center border"
              style={{
                backgroundColor: `${colors.error}15`,
                borderColor: colors.error,
              }}
            >
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: colors.error }}>
                Sin Acceso
              </h3>
              <p style={{ color: colors.error }}>
                No tienes acceso a ninguna función en este momento.
                <br />
                Por favor, contacta al administrador para que revise tus permisos.
              </p>
            </div>
          )}

          {/* Información adicional */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Nota</h3>
            <p className="text-blue-800 text-sm">
              El acceso a funciones está controlado por permisos específicos. Si necesitas
              acceso a funciones adicionales, por favor contacta al administrador del
              sistema.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DefaultDashboard
